import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStateContext } from '../../../contexts/ContextProvider';
import { NavSection } from './types';
import { canAccess } from './staffAccess';
import { STAFF_NAV, isStaffNavItemActive, staffNavItemCount } from './staffNav';
import { useStaffQueueCounts } from './staffQueues';

/**
 * Menu back-office, affiché en mode super admin, construit depuis `STAFF_NAV`.
 *
 * Une entrée n'apparaît que si l'utilisateur a le droit d'ouvrir son écran, et
 * sa pastille vient du registre des files (`staffQueues.ts`), qui ne sonde
 * jamais une file hors des droits.
 *
 * @param enabled faux hors mode super admin : les compteurs ne sont plus
 *                interrogés toutes les minutes pendant une démo, puisque leurs
 *                pastilles sont cachées.
 */
export function useStaffMenuSections(enabled: boolean) {
    const { t } = useTranslation('navigation');
    const { user } = useStateContext();
    const counts = useStaffQueueCounts(enabled, { poll: true });

    const isSuperAdmin = Boolean(user?.is_super_admin);
    const canProspects = Boolean(user?.can_access_crm_prospects);
    const canClients = Boolean(user?.can_access_crm_clients);

    return useMemo((): NavSection[] => {
        const access = { is_super_admin: isSuperAdmin, can_access_crm_prospects: canProspects, can_access_crm_clients: canClients };
        if (!canAccess(access, 'crm:any')) return [];

        return STAFF_NAV
            .map((section) => ({
                key: section.key,
                label: section.labelKey ? t(section.labelKey) : undefined,
                items: section.items
                    .filter((item) => canAccess(access, item.access))
                    .map((item) => ({
                        key: item.key,
                        link: item.link,
                        icon: item.icon,
                        text: t(item.labelKey),
                        badgeCount: staffNavItemCount(item, counts),
                        isActive: (pathname: string, search: string) => isStaffNavItemActive(item, pathname, search),
                    })),
            }))
            .filter((section) => section.items.length > 0);
    }, [t, isSuperAdmin, canProspects, canClients, counts]);
}
